import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Check, LifeBuoy, MessageCircle, Plus, Send } from "lucide-react";
import { api } from "../api";

export const SupportCenter = ({ onBack }) => {
  const [tickets, setTickets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const threadRef = useRef(null);

  const load = useCallback(async (preferredId = "") => {
    const { data } = await api.get("/support/tickets");
    setTickets(data);
    setSelectedId((current) => {
      const wanted = preferredId || current;
      return data.some((ticket) => ticket.id === wanted) ? wanted : (data[0]?.id || "");
    });
  }, []);

  useEffect(() => {
    load().catch(() => setError("Could not connect to Support team."));
    const timer = window.setInterval(() => load().catch(() => null), 3000);
    return () => window.clearInterval(timer);
  }, [load]);

  const selected = tickets.find((ticket) => ticket.id === selectedId);
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [selected?.messages?.length, selectedId]);

  const sendMessage = async (event) => {
    event.preventDefault();
    if (!message.trim()) return;
    setBusy(true); setError("");
    try {
      if (!selected || selected.status === "closed") {
        const { data } = await api.post("/support/tickets", { subject: "Support chat", message: message.trim() });
        setMessage("");
        await load(data.ticket.id);
      } else {
        await api.post(`/support/tickets/${selected.id}/messages`, { message: message.trim() });
        setMessage("");
        await load(selected.id);
      }
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Message could not be sent.");
    } finally { setBusy(false); }
  };

  return (
    <main className="page support-chat-page" data-testid="user-support-page">
      <div className="page-heading support-chat-heading">
        <div>
          <div className="eyebrow">MY TICKETS · HELP</div>
          <h1>Support</h1>
        </div>
        <button className="ghost" data-testid="support-back-button" onClick={onBack}><ArrowLeft size={15} /> My tickets</button>
      </div>
      <section className="support-chat-shell" data-testid="support-chat-shell">
        <aside className="support-chat-history" data-testid="user-support-ticket-list">
          <div className="support-history-head">
            <span>CHATS</span>
            <button className="icon-btn support-new-chat" title="New support chat" data-testid="support-new-chat-button" onClick={() => { setSelectedId(""); setMessage(""); }}><Plus size={17} /></button>
          </div>
          {tickets.length === 0 ? (
            <div className="support-empty" data-testid="support-ticket-list-empty">Your first message will start a support chat.</div>
          ) : tickets.map((ticket) => (
            <button className={`support-ticket-row${selectedId === ticket.id ? " active" : ""}`} key={ticket.id} data-testid={`support-ticket-row-${ticket.id}`} onClick={() => setSelectedId(ticket.id)}>
              <strong>Support team</strong>
              <span>{ticket.messages.at(-1)?.message}</span>
              <small>{new Date(ticket.updated_at).toLocaleString()}</small>
            </button>
          ))}
        </aside>
        <div className="support-chat-main">
          <header className="support-chat-contact" data-testid="support-chat-contact">
            <div className="support-avatar"><LifeBuoy size={19} /></div>
            <div><strong>Support team</strong><span><i /> Connected</span></div>
            {selected && <b className={`support-status status-${selected.status}`} data-testid={`support-ticket-status-${selected.id}`}>{selected.status}</b>}
          </header>
          <div className="support-messages whatsapp-thread" ref={threadRef} data-testid="support-conversation-thread">
            {!selected ? (
              <div className="support-welcome" data-testid="support-chat-welcome">
                <MessageCircle size={26} />
                <strong>How can we help?</strong>
                <span>Write your message below to start chatting with the Support team.</span>
              </div>
            ) : selected.messages.map((item) => (
              <article className={`support-message ${item.sender}`} key={item.id} data-testid={`support-message-${item.id}`}>
                <div className="support-message-meta">
                  <strong data-testid={`support-message-sender-${item.id}`}>{item.sender === "support" ? "Support team" : "You"}</strong>
                  <time>{new Date(item.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</time>
                </div>
                <p>{item.message}</p>
                {item.sender === "user" && <Check className="message-check" size={13} />}
              </article>
            ))}
          </div>
          {error && <div className="error support-chat-error" role="alert" data-testid="support-error">{error}</div>}
          <form className="support-chat-composer" onSubmit={sendMessage} data-testid="support-chat-form">
            <textarea
              data-testid="support-message-input"
              value={message}
              maxLength={2000}
              onChange={(event) => setMessage(event.target.value)}
              placeholder={selected?.status === "closed" ? "Write to start a new support chat..." : "Type a message..."}
              aria-label="Message Support team"
              required
            />
            <button className="primary support-chat-send" data-testid="support-send-message-button" disabled={busy || !message.trim()} aria-label="Send message">
              <Send size={18} />
            </button>
          </form>
        </div>
      </section>
    </main>
  );
};